import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";

export default function ProSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "pending", attempts: 0 });
  const navigate = useNavigate();
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/app/pro");
      return;
    }
    if (polled.current) return;
    polled.current = true;

    let attempts = 0;
    const maxAttempts = 12;
    const interval = 2500;

    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        attempts += 1;
        setState({ status: "pending", attempts });
        if (attempts >= maxAttempts) {
          setState({ status: "timeout" });
          return;
        }
        setTimeout(poll, interval);
      } catch {
        setState({ status: "error" });
      }
    };
    poll();
  }, [sessionId, navigate]);

  return (
    <div className="max-w-xl mx-auto py-20 text-center" data-testid="pro-success-page">
      {state.status === "pending" && (
        <>
          <div className="flex justify-center gap-2 mb-6">
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--primary))] simmer" />
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--secondary))] simmer-2" />
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--primary))] simmer-3" />
          </div>
          <p className="font-serif italic text-3xl">Confirmando tu pago…</p>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Intento {state.attempts + 1} de 12</p>
        </>
      )}

      {state.status === "paid" && (
        <div className="fade-up">
          <CheckCircle size={80} weight="duotone" className="mx-auto text-[hsl(var(--secondary))]" />
          <p className="mt-6 font-serif text-5xl">¡Bienvenido a MIZETA Pro!</p>
          <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed">
            Tu acceso está activo. Ya puedes extraer todas las recetas que quieras.
          </p>
          <button
            onClick={() => navigate("/app/extract")}
            data-testid="pro-success-extract-btn"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--primary))] text-white font-semibold btn-press hover-lift"
          >
            Extraer una receta <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}

      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <div>
          <WarningCircle size={64} weight="duotone" className="mx-auto text-[hsl(var(--destructive))]" />
          <p className="mt-6 font-serif text-3xl">No hemos podido confirmar tu pago</p>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
            Si has completado el pago, espera unos segundos y refresca. Si no, vuelve a intentarlo.
          </p>
          <button onClick={() => navigate("/app/pro")} className="mt-6 px-6 py-3 rounded-full bg-[hsl(var(--foreground))] text-white btn-press">
            Volver a planes
          </button>
        </div>
      )}
    </div>
  );
}
